import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import {
  MOMENT_DATE_FORMAT,
  MOMENT_DATE_TIME_FORMAT_SLASH,
} from '@app/app.constants';
import { PdfPreviewTemplateStateService } from '@app/modules/pdf-request-form/services/pdf-preview-template-state.service';
import { SampleFields } from '@app/modules/service-data/components/sample/sample-dialog/field-sets/sample-fields.enum';
import { ServiceDataFields } from '@app/modules/service-data/components/service-data/service-data-form/field-sets/service-data-fields.enum';
import { getDateOnly } from '@app/modules/service-data/service-data.utils';
import { SampleUtilsService } from '@app/modules/service-data/services/sample-utils.service';
import { ServiceDataUtilsService } from '@app/modules/service-data/services/service-data-utils.service';
import { SharedSample } from '@app/modules/service-data/types/sample.types';
import {
  CancerHistoryStatus,
  ExtendedProductInventoryItem,
  PregnancyType,
  PriorScreeningTestType,
  ServiceData,
  SharedServiceData,
} from '@app/modules/service-data/types/service-data.types';
import { DataInputType, IncidentalFindingsReporting } from '@core/core.types';
import { anonymizeName, calculateSimpleCode } from '@core/utils/core.util';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-pdf-preview-template',
  templateUrl: './pdf-preview-template.component.html',
  styleUrls: ['./pdf-preview-template.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgTemplateOutlet, AsyncPipe, TranslatePipe],
})
export class PdfPreviewTemplateComponent {
  protected serviceDataUtilsService = inject(ServiceDataUtilsService);
  private state = inject(PdfPreviewTemplateStateService);
  private sampleUtilsService = inject(SampleUtilsService);
  private translateService = inject(TranslateService);

  protected moment = moment;
  protected readonly MOMENT_DATE_FORMAT = MOMENT_DATE_FORMAT;
  protected readonly MOMENT_DATE_TIME_FORMAT_SLASH =
    MOMENT_DATE_TIME_FORMAT_SLASH;
  protected readonly getDateOnly = getDateOnly;
  protected anonymizeName = anonymizeName;

  protected simpleCode: string;

  protected pii: ExtendedProductInventoryItem;
  protected sample: SharedSample;
  protected serviceData: SharedServiceData;
  protected previousSampleKitId: string;

  protected patientBMI$: Observable<boolean>;

  protected CancerHistoryStatus = CancerHistoryStatus;
  protected PregnancyType = PregnancyType;
  protected PriorScreeningTestType = PriorScreeningTestType;

  protected selectedDataInputTypes: DataInputType[];
  protected fieldSet: string[];
  protected sampleFieldSet: string[];

  protected chapterTitles: { [key: string]: string } = {};

  constructor() {
    const state = this.state;

    this.pii = state.options.pii;
    this.previousSampleKitId = state.options.previousSampleKitId;
    this.selectedDataInputTypes = state.options.selectedDataInputTypes;
    this.fieldSet = state.options.fieldSet;
    this.sampleFieldSet = state.options.sampleFieldSet;
    this.serviceData = this.serviceDataUtilsService.serviceDataToForm(
      this.pii.sample.serviceData,
      this.selectedDataInputTypes
    );
    this.sample = this.sampleUtilsService.sampleToForm(
      this.pii.sample,
      this.selectedDataInputTypes
    );
    this.simpleCode = calculateSimpleCode(this.pii.kitId);

    this.generateChapterTitles();
  }

  protected getFullName(serviceData: ServiceData): string {
    return (
      serviceData.patientInformation.firstName +
      ' ' +
      serviceData.patientInformation.lastName
    );
  }

  private generateChapterTitles(): void {
    let chapterNumber = 1;

    this.chapterTitles['patientInformation'] =
      `${chapterNumber}. ${this.translateService.instant(
        'pdfRequestForm.chapters.patientInformation'
      )}`;
    chapterNumber++;

    if (this.fieldSet.includes(ServiceDataFields.PregnancyInformation)) {
      this.chapterTitles['currentPregnancy'] =
        `${chapterNumber}. ${this.translateService.instant(
          'pdfRequestForm.chapters.currentPregnancy'
        )}`;
      chapterNumber++;
    }

    if (this.fieldSet.includes(ServiceDataFields.PatientHistoryInformation)) {
      this.chapterTitles['medicalData'] =
        `${chapterNumber}. ${this.translateService.instant(
          'pdfRequestForm.chapters.medicalData'
        )}`;
      chapterNumber++;
    }

    this.chapterTitles['sampling'] =
      `${chapterNumber}. ${this.translateService.instant(
        'pdfRequestForm.chapters.sampling'
      )}`;
    chapterNumber++;

    if (this.fieldSet.includes(ServiceDataFields.GenderInformation)) {
      this.chapterTitles['genderIdentification'] =
        `${chapterNumber}. ${this.translateService.instant(
          'pdfRequestForm.chapters.genderIdentification'
        )}`;
      chapterNumber++;
    }

    this.chapterTitles['comments'] =
      `${chapterNumber}. ${this.translateService.instant(
        'pdfRequestForm.chapters.comments'
      )}`;
  }

  protected readonly ServiceDataFields = ServiceDataFields;
  protected readonly SampleFields = SampleFields;
  protected readonly IncidentalFindingsReporting = IncidentalFindingsReporting;
}
